'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useIsSettingsContentPanelActive } from '@terra/ui-design-system';

const SettingsTabHeaderActionsStateContext = createContext<ReactNode>(null);
const SettingsTabHeaderActionsDispatchContext = createContext<Dispatch<
  SetStateAction<ReactNode>
> | null>(null);

export function SettingsTabHeaderActionsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [actions, setActions] = useState<ReactNode>(null);

  return (
    <SettingsTabHeaderActionsDispatchContext.Provider value={setActions}>
      <SettingsTabHeaderActionsStateContext.Provider value={actions}>
        {children}
      </SettingsTabHeaderActionsStateContext.Provider>
    </SettingsTabHeaderActionsDispatchContext.Provider>
  );
}

export function useSettingsTabHeaderChildActions() {
  return useContext(SettingsTabHeaderActionsStateContext);
}

function useSettingsTabHeaderActionsDispatch() {
  const setActions = useContext(SettingsTabHeaderActionsDispatchContext);

  if (!setActions) {
    throw new Error(
      'Settings tab header hooks must be used within SettingsTabHeaderActionsProvider',
    );
  }

  return setActions;
}

export function useSettingsTabHeaderActions(actions: ReactNode) {
  const setActions = useSettingsTabHeaderActionsDispatch();
  const isPanelActive = useIsSettingsContentPanelActive();

  useEffect(() => {
    if (!isPanelActive) {
      return;
    }

    setActions((current) => (current === actions ? current : actions));

    return () => {
      setActions((current) => (current === null ? current : null));
    };
  }, [actions, isPanelActive, setActions]);
}
